import { useMemo } from 'react'
import { API_BASE_URL, AuthError, apiFetch } from '@/api/client'
import { useAuth } from '@/hooks/use-auth'

/** Every staff Quote Builder endpoint is the demo path re-rooted here. */
export const QUOTE_BUILDER_ROOT = '/dashboard/quote-builder'

type ApiErrorBody = {
  detail?: { code?: string; current_revision?: number; [key: string]: unknown } | string
}

/**
 * A failed Quote Builder request. `code` is the demo's `{"detail": {"code": …}}` error code, so
 * stage code can branch on it (`revision_conflict`, …) exactly as the demo does.
 */
export class QuoteBuilderApiError extends Error {
  status: number
  code: string
  body: ApiErrorBody

  constructor(status: number, body: ApiErrorBody) {
    const code =
      (typeof body.detail === 'string' ? body.detail : body.detail?.code) ||
      `request_failed_${status}`
    super(code.replaceAll('_', ' '))
    this.name = 'QuoteBuilderApiError'
    this.status = status
    this.code = code
    this.body = body
  }
}

export type QuoteBuilderRequest = <T>(path: string, init?: RequestInit) => Promise<T>

export type QuoteBuilderApi = {
  /** JSON request to `/dashboard/quote-builder{path}` as the signed-in staff user. */
  request: QuoteBuilderRequest
  /** Raw response, for downloads (PDFs, CSVs). */
  fetch: (path: string, init?: RequestInit) => Promise<Response>
}

export function createQuoteBuilderApi(getAccessToken: () => Promise<string | null>): QuoteBuilderApi {
  const send = async (path: string, init: RequestInit = {}) => {
    const token = await getAccessToken()
    if (!token) throw new AuthError(401, 'Failed to acquire access token')
    const headers = new Headers(init.headers)
    headers.set('Authorization', `Bearer ${token}`)
    if (!headers.has('Accept')) headers.set('Accept', 'application/json')
    if (init.body && !headers.has('Content-Type') && !(init.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json')
    }
    return apiFetch(`${API_BASE_URL}${QUOTE_BUILDER_ROOT}${path}`, { ...init, headers })
  }

  return {
    fetch: send,
    request: async <T,>(path: string, init?: RequestInit) => {
      const response = await send(path, init)
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as ApiErrorBody
        throw new QuoteBuilderApiError(response.status, body)
      }
      if (response.status === 204) return undefined as T
      return (await response.json()) as T
    },
  }
}

/** The Quote Builder API for the signed-in user. Stable across renders. */
export function useQuoteBuilderApi(): QuoteBuilderApi {
  const { getAccessToken } = useAuth()
  return useMemo(() => createQuoteBuilderApi(getAccessToken), [getAccessToken])
}
